import { searchSwitchViewClick } from 'analytics/eventDefinitions'
import { addGTMEvent } from 'api/googleTagManager'
import { pushWithRegion } from 'actions/NavigationActions'
import Clickable from 'components/Common/Clickable'
import ClickableLink from 'components/Common/Clickable/ClickableLink'
import IconButton from 'LuxKit/Button/IconButton'
import LineArrowLeftIcon from 'LuxKit/Icons/line/LineArrowLeftIcon'
import LineMapIcon from 'LuxKit/Icons/line/LineMapIcon'
import LineSearchIcon from 'LuxKit/Icons/line/LineSearchIcon'
import useGlobalSearchURLHashFilter from 'hooks/GlobalSearch/useGlobalSearchURLHashFilter'
import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import LogoIcon from 'components/App/Header/Logo/LogoIcon'
import NavBarUserAvatar from 'components/NavKit/NavBar/NavBarUserAvatar'
import NavBarUserButton from 'components/NavKit/NavBarButtons/NavBarUserButton'
import NavBarLoginButton from 'components/NavKit/NavBarButtons/NavBarLoginButton'
import AccountMenuDrawer from 'components/NavKit/AccountMenu/AccountMenuDrawer'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import { closeLoyaltyRewardsNotification } from 'loyalty/components/LoyaltyRewardsNotification'
import { prettyFlexibleDates } from 'components/Search/SearchForm/SearchDateInput/FlexibleDateSearchField'
import { prettyDates, getTotalRoomsString, getAppliedFilterCount } from 'components/Search/utils'
import { getHotelEnabledFilters } from 'selectors/filters/filterSelectors'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import config from 'constants/config'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import ModalContext from 'contexts/ModalContext'
import { navBrandLogoProductLabel } from 'i18n/productLabels'
import { formatOccupantsAsGuests } from 'lib/offer/occupancyUtils'
import { rem } from 'polished'
import clsx from 'clsx'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { useAppNavigation } from 'lib/routing/useAppNavigation'
import { selectLoggedIn } from 'selectors/accountSelectors'
import type { ComponentProps, MouseEventHandler } from 'react'
import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import styled from 'styled-components'
import AIGlowBorder from 'components/Search/SearchForm/AIGlowBorder'
import HotelExposedFiltersHeader from './HotelExposedFiltersHeader/HotelExposedFiltersHeader'
import { mediaQueryUp } from 'components/utils/breakpoint'
import NavRedesignSearchRow from './NavRedesignSearchRow'
import { deleteSearchParams } from 'lib/url/searchUrlUtils'
import type { OfferListSortOption } from 'constants/offerListFilters'

const DESCRIPTION_SEPARATOR = '•'

const Container = styled.div`
  padding: ${rem(8)} ${rem(20)} ${rem(12)};
  > * + * {
    margin-top: ${rem(12)};
  }

  &.nav-redesign {
    padding: ${rem(12)} ${rem(16)};

    ${mediaQueryUp.tablet} {
      padding: ${rem(16)} ${rem(20)} ${rem(12)};
    }

    ${mediaQueryUp.desktop} {
      padding-inline: ${rem(32)};
    }
  }
`

const InlineContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: ${rem(8)};
`

const BrandLink = styled(ClickableLink)`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  height: ${rem(32)};

  ${mediaQueryUp.desktop} {
    height: ${rem(24)};
  }
`

const AccountEntry = styled(Clickable)`
  flex-shrink: 0;
  display: flex;
  align-items: center;
`

const EditContainer = styled(Clickable)`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${rem(8)};
  width: 100%;
  min-width: 0;
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
`

const PillEditContainer = styled(Clickable)`
  display: flex;
  align-items: center;
  gap: ${rem(12)};
  padding: ${rem(8)} ${rem(12)};
  width: 100%;
  min-width: 0;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
  border-radius: ${props => props.theme.borderRadius.round};

  ${mediaQueryUp.tablet} {
    min-height: ${rem(48)};
    padding-inline: ${rem(16)};
    justify-content: center;
  }
`

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const PillStacked = styled.div`
  min-width: 0;
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const PillInline = styled.div`
  display: none;
  min-width: 0;
  ${mediaQueryUp.tablet} {
    display: block;
  }
`

const PillInlineLabel = styled.span`
  font-weight: ${props => props.theme.font.primary.weight.bold};
`

const AccountCompact = styled.div`
  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const AccountFull = styled.div`
  display: none;
  ${mediaQueryUp.desktop} {
    display: block;
  }
`

const LoginIconOnly = styled.div`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const LoginButtonWrap = styled.div`
  display: none;
  ${mediaQueryUp.tablet} {
    display: block;
  }
`

const DescriptionSeparator = styled.span`
  margin: 0 ${rem(8)};
`

const StyledLineSearchIcon = styled(LineSearchIcon)`
  flex: 0 0 auto;
`

const StyledIconButton = styled(IconButton)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

function Description({ primaryText, secondaryText }: { primaryText: string, secondaryText?: string }) {
  const content = secondaryText ? (
    <>
      {primaryText}
      <DescriptionSeparator>{DESCRIPTION_SEPARATOR}</DescriptionSeparator>
      {secondaryText}
    </>
  ) : primaryText

  return <Caption variant="large" wrap="truncate">{content}</Caption>
}

function SearchBarSummary({ label, primaryText, secondaryText }: { label: string, primaryText: string, secondaryText?: string }) {
  return (
    <TextContainer>
      <Heading variant="heading6" wrap="truncate">{label}</Heading>
      <Description primaryText={primaryText} secondaryText={secondaryText} />
    </TextContainer>
  )
}

interface MappedStateProps {
  windowSearch: string
}

type InheritedHotelExposedFiltersHeaderProps = Pick<ComponentProps<typeof HotelExposedFiltersHeader>, 'onSortChange' | 'onFilterChange'>

interface Props extends InheritedHotelExposedFiltersHeaderProps {
  onEdit: () => void
  filters: App.OfferListFilters
  sortOptions?: Array<OfferListSortOption>
  className?: string
  hideFilters?: boolean
  overrideAvailableFilters?: App.OfferListFilterOptions
  showFilters?: boolean
  isMapView?: boolean
  navRedesign?: boolean
  showBrandAndAccount?: boolean
  switchView?: {
    label: string
    view: 'map' | 'list'
    URL: string
    goBack?: boolean
  }
}

function SearchField(props: Props & MappedStateProps) {
  const {
    onEdit,
    filters,
    windowSearch,
    sortOptions,
    className,
    overrideAvailableFilters,
    onFilterChange,
    onSortChange,
    showFilters = true,
    isMapView = false,
    navRedesign = false,
    showBrandAndAccount = false,
    switchView,
  } = props
  const intl = useIntl()
  const dispatch = useAppDispatch()
  const navigation = useAppNavigation()
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const showModal = useContext(ModalContext)
  const { toggleFilterPanel } = useGlobalSearchURLHashFilter()
  const openAccountDrawer = useCallback(() => {
    closeLoyaltyRewardsNotification()
    showModal(<AccountMenuDrawer />)
  }, [showModal])
  const openLoginModal = useCallback(() => showModal(<AccountAccessModal />), [showModal])

  const enabledFilters = useAppSelector(getHotelEnabledFilters)
  const filterCount = getAppliedFilterCount(filters, enabledFilters, true)

  const filterLabel = intl.formatMessage({
    id: 'hsWWbC14Zev6EBQh82oq',
    defaultMessage: 'Filter',
    description: 'Filter button label on the mobile map search header',
  })

  const accountLabel = intl.formatMessage({
    id: 'yruRQ-7AtFBKNQa-80cD',
    defaultMessage: 'Account',
    description: 'Fallback display name in navigation when user given name is not available',
  })

  const loginLabel = intl.formatMessage({
    id: 'PzcSUqTGzjiNHFXJC3x_',
    defaultMessage: 'Log in',
    description: 'Login button text in navigation bar on the login or signup page',
  })

  const accountEntry = isLoggedIn ? (
    <>
      <AccountCompact>
        <AccountEntry onClick={openAccountDrawer} aria-label={accountLabel}>
          <NavBarUserAvatar />
        </AccountEntry>
      </AccountCompact>
      <AccountFull>
        <NavBarUserButton />
      </AccountFull>
    </>
  ) : (
    <>
      <LoginIconOnly>
        <AccountEntry onClick={openLoginModal} aria-label={loginLabel}>
          <NavBarUserAvatar />
        </AccountEntry>
      </LoginIconOnly>
      <LoginButtonWrap>
        <NavBarLoginButton />
      </LoginButtonWrap>
    </>
  )

  const {
    checkinDate,
    checkoutDate,
    occupancies,
    searchTargetLabel,
    dateSearchOptionId,
    flexibleMonths,
    flexibleNights,
    userSelectedFlexibleMonths,
  } = useContext(GlobalSearchStateContext)

  const mapViewURL = useMemo(() => `/search/map?${deleteSearchParams(windowSearch, 'sortBy')}`, [windowSearch])

  const anytimeText = intl.formatMessage({
    id: 'X3uzzIDkx0fX22D7UmQU',
    defaultMessage: 'Anytime',
    description: 'Label shown when no specific date is selected for search',
  })

  const mapAreaText = intl.formatMessage({
    id: 'xP3ZFVMbgOILlHR2q-Ll',
    defaultMessage: 'Map area',
    description: 'Fallback label when no search target location is specified',
  })

  const mapText = intl.formatMessage({
    id: 'dgZ0q4-mlU-8LQv1Cntf',
    defaultMessage: 'Map',
    description: 'Button label to switch to the map view display in the search results interface',
  })

  const listViewAriaLabel = intl.formatMessage({
    id: 'WBjNYbkwGBMPU9zKBSTt',
    defaultMessage: 'list-view',
    description: 'Accessibility aria-label for the list view button in the search interface',
  })

  const handleBackToList = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    addGTMEvent(searchSwitchViewClick())

    if (switchView) {
      if (switchView.goBack) {
        navigation.goBack()
        return
      }

      dispatch(pushWithRegion(switchView.URL))
    }
  }, [switchView, navigation, dispatch])

  const searchBarDescription = useMemo(() => {
    const occupancyWithRoomDetails = occupancies ?
      `${formatOccupantsAsGuests(...occupancies)} ${getTotalRoomsString(intl, occupancies)}` :
      undefined
    if (dateSearchOptionId === DATE_SEARCH_OPTION_IDS.ANYTIME) {
      return {
        primaryText: anytimeText,
        secondaryText: occupancyWithRoomDetails,
      }
    }
    const datesDetails = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.FLEXIBLE ?
        prettyFlexibleDates(userSelectedFlexibleMonths ? flexibleMonths : undefined, flexibleNights, undefined, intl) :
        prettyDates(intl, checkinDate, checkoutDate)
    return {
      primaryText: datesDetails,
      secondaryText: occupancyWithRoomDetails,
    }
  }, [dateSearchOptionId, userSelectedFlexibleMonths, flexibleMonths, flexibleNights, checkinDate, checkoutDate, occupancies, anytimeText, intl])
  const summaryLabel = searchTargetLabel ?? mapAreaText

  const pillInlineDetails = [searchBarDescription.primaryText, searchBarDescription.secondaryText]
    .filter(Boolean)
    .join(` ${DESCRIPTION_SEPARATOR} `)

  const navRedesignSearchBar = (
    <PillEditContainer onClick={onEdit}>
      <StyledLineSearchIcon size={20} />
      <PillStacked>
        <SearchBarSummary label={summaryLabel} primaryText={searchBarDescription.primaryText} secondaryText={searchBarDescription.secondaryText} />
      </PillStacked>
      <PillInline>
        <Caption variant="large" wrap="truncate">
          <PillInlineLabel>{summaryLabel}</PillInlineLabel>
          {!!pillInlineDetails && <DescriptionSeparator>{DESCRIPTION_SEPARATOR}</DescriptionSeparator>}
          {pillInlineDetails}
        </Caption>
      </PillInline>
    </PillEditContainer>
  )

  const legacySearchBar = (
    <EditContainer onClick={onEdit}>
      <SearchBarSummary label={summaryLabel} primaryText={searchBarDescription.primaryText} secondaryText={searchBarDescription.secondaryText} />
      <StyledLineSearchIcon size={20} />
    </EditContainer>
  )

  const brandLink = showBrandAndAccount ? (
    <BrandLink to={config.header.logo.to} aria-label={intl.formatMessage(navBrandLogoProductLabel[config.BRAND])}>
      <LogoIcon mobileVariant="icon" tabletVariant="icon" desktopVariant="icon+text" extraLargeDesktopVariant="icon+text" height="100%" width={null} />
    </BrandLink>
  ) : null

  return (
    <Container className={clsx(className, { 'nav-redesign': navRedesign })}>
      <InlineContainer>
        {isMapView && switchView?.view === 'list' && (
          <Clickable
            onClick={handleBackToList}
            aria-label={listViewAriaLabel}
            data-testid="list-view"
          >
            <LineArrowLeftIcon />
          </Clickable>
        )}
        {navRedesign ? (
          <NavRedesignSearchRow
            searchBar={navRedesignSearchBar}
            brandLink={brandLink}
            accountEntry={showBrandAndAccount ? accountEntry : null}
            isMapView={isMapView}
            filterCount={filterCount}
            filterLabel={filterLabel}
            onToggleFilter={toggleFilterPanel}
          />
        ) : (
          <>
            <AIGlowBorder>{legacySearchBar}</AIGlowBorder>
            {!isMapView && (
              <StyledIconButton kind="tertiary" to={mapViewURL}>
                <LineMapIcon />
                <BodyText variant="small">{mapText}</BodyText>
              </StyledIconButton>
            )}
          </>
        )}
      </InlineContainer>
      {showFilters && <HotelExposedFiltersHeader
        filters={filters}
        overrideAvailableFilters={overrideAvailableFilters}
        showSort
        sortOptions={sortOptions}
        onFilterChange={onFilterChange}
        onSortChange={onSortChange}
        mapViewUrl={navRedesign && !isMapView ? mapViewURL : undefined}
      />}
    </Container>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>((appState): MappedStateProps => ({
  windowSearch: appState.router.location.search,
}))(SearchField)
